import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Switch,
  FormControlLabel,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Autocomplete,
  Slider,
  InputAdornment
} from '@mui/material';
import {
  Add,
  Delete,
  Save,
  AddCircle,
  Code,
  Description,
  Quiz,
  Upload,
  Link as LinkIcon,
  Work
} from '@mui/icons-material';
import axios from 'axios';

const CreateTask = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [jobs, setJobs] = useState([]);
  const [selectedJobs, setSelectedJobs] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'other',
    difficulty: 'medium',
    taskType: 'essay',
    instructions: '',
    duration: {
      value: 168, // Default 168 hours (7 days)
      unit: 'hours'
    },
    deadline: '',
    maxScore: 100,
    passingScore: 60,
    autoEvaluate: false,
    pipelineStage: 'Assessment'
  });

  // Multiple choice questions
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState({
    question: '',
    options: ['', '', '', ''],
    correctAnswer: 0,
    points: 10
  });

  // Code template
  const [codeTemplate, setCodeTemplate] = useState({
    language: 'javascript',
    starterCode: '',
    testCases: []
  });

  // Evaluation criteria
  const [evaluationCriteria, setEvaluationCriteria] = useState([]);
  const [currentCriterion, setCurrentCriterion] = useState({
    criterion: '',
    weight: 25,
    description: ''
  });

  // Resources
  const [resources, setResources] = useState([]);
  const [currentResource, setCurrentResource] = useState({
    title: '',
    url: '',
    resourceType: 'link'
  });

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('http://localhost:5000/api/jobs', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(response.data.data || []);
    } catch (err) {
      console.error('Error fetching jobs:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData({
      ...formData,
      [name]: e.target.type === 'checkbox' ? checked : value
    });
  };

  const handleAddQuestion = () => {
    if (currentQuestion.question && currentQuestion.options.every(opt => opt)) {
      setQuestions([...questions, currentQuestion]);
      setCurrentQuestion({
        question: '',
        options: ['', '', '', ''],
        correctAnswer: 0,
        points: 10
      });
    } else {
      setError('Please fill in all question fields');
    }
  };

  const handleDeleteQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const handleAddTestCase = () => {
    setCodeTemplate({
      ...codeTemplate,
      testCases: [...codeTemplate.testCases, {
        input: '',
        expectedOutput: '',
        isHidden: false
      }]
    });
  };

  const handleDeleteTestCase = (index) => {
    setCodeTemplate({
      ...codeTemplate,
      testCases: codeTemplate.testCases.filter((_, i) => i !== index)
    });
  };

  const handleAddCriterion = () => {
    if (currentCriterion.criterion && currentCriterion.description) {
      setEvaluationCriteria([...evaluationCriteria, currentCriterion]);
      setCurrentCriterion({
        criterion: '',
        weight: 25,
        description: ''
      });
    } else {
      setError('Please fill in criterion details');
    }
  };

  const handleDeleteCriterion = (index) => {
    setEvaluationCriteria(evaluationCriteria.filter((_, i) => i !== index));
  };

  const handleAddResource = () => {
    if (currentResource.title && currentResource.url) {
      setResources([...resources, currentResource]);
      setCurrentResource({
        title: '',
        url: '',
        resourceType: 'link'
      });
    } else {
      setError('Please fill in resource details');
    }
  };

  const handleDeleteResource = (index) => {
    setResources(resources.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      // Prepare task data based on type
      let taskData = {
        ...formData,
        mappedJobs: selectedJobs.map(job => job._id),
        evaluationCriteria,
        resources
      };

      // Add type-specific data
      if (formData.taskType === 'multiple-choice') {
        if (questions.length === 0) {
          throw new Error('Please add at least one question');
        }
        taskData.questions = questions;
      } else if (formData.taskType === 'code') {
        if (!codeTemplate.starterCode || codeTemplate.testCases.length === 0) {
          throw new Error('Please provide starter code and test cases');
        }
        taskData.codeTemplate = codeTemplate;
      }

      const response = await axios.post(
        'http://localhost:5000/api/tasks',
        taskData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      navigate('/tasks');
    } catch (err) {
      console.error('Error creating task:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error details:', JSON.stringify(err.response?.data, null, 2));
      
      // Display detailed validation errors if available
      if (err.response?.data?.errors) {
        const validationErrors = err.response.data.errors.map(e => `${e.path || e.param}: ${e.msg}`).join(', ');
        setError(`Validation failed: ${validationErrors}`);
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to create task');
      }
    } finally {
      setLoading(false);
    }
  };

  const getTaskTypeIcon = (type) => {
    const icons = {
      'multiple-choice': <Quiz />,
      'essay': <Description />,
      'code': <Code />,
      'file-upload': <Upload />,
      'url-submission': <LinkIcon />,
      'project': <Work />
    };
    return icons[type] || <Description />;
  };

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom>
        Create New Task
      </Typography>
      
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Task Title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  label="Category"
                >
                  <MenuItem value="coding">Coding</MenuItem>
                  <MenuItem value="design">Design</MenuItem>
                  <MenuItem value="writing">Writing</MenuItem>
                  <MenuItem value="analytical">Analytical</MenuItem>
                  <MenuItem value="behavioral">Behavioral</MenuItem>
                  <MenuItem value="case-study">Case Study</MenuItem>
                  <MenuItem value="presentation">Presentation</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Difficulty</InputLabel>
                <Select
                  name="difficulty"
                  value={formData.difficulty}
                  onChange={handleChange}
                  label="Difficulty"
                >
                  <MenuItem value="easy">Easy</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="hard">Hard</MenuItem>
                  <MenuItem value="expert">Expert</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Task Type</InputLabel>
                <Select
                  name="taskType"
                  value={formData.taskType}
                  onChange={handleChange}
                  label="Task Type"
                  startAdornment={getTaskTypeIcon(formData.taskType)}
                >
                  <MenuItem value="multiple-choice">Multiple Choice</MenuItem>
                  <MenuItem value="essay">Essay/Writing</MenuItem>
                  <MenuItem value="code">Coding</MenuItem>
                  <MenuItem value="file-upload">File Upload</MenuItem>
                  <MenuItem value="url-submission">URL Submission</MenuItem>
                  <MenuItem value="project">Project</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Instructions"
                name="instructions"
                value={formData.instructions}
                onChange={handleChange}
                placeholder="Provide clear instructions for candidates"
              />
            </Grid>
            
            <Divider sx={{ width: '100%', my: 2 }} />
            
            {/* Task Type Specific Content */}
            {formData.taskType === 'multiple-choice' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Multiple Choice Questions
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Question"
                    value={currentQuestion.question}
                    onChange={(e) => setCurrentQuestion({
                      ...currentQuestion,
                      question: e.target.value
                    })}
                  />
                </Grid>
                
                {currentQuestion.options.map((option, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <TextField
                      fullWidth
                      label={`Option ${index + 1}`}
                      value={option}
                      onChange={(e) => {
                        const newOptions = [...currentQuestion.options];
                        newOptions[index] = e.target.value;
                        setCurrentQuestion({
                          ...currentQuestion,
                          options: newOptions
                        });
                      }}
                      InputProps={{
                        startAdornment: (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={currentQuestion.correctAnswer === index}
                                onChange={() => setCurrentQuestion({
                                  ...currentQuestion,
                                  correctAnswer: index
                                })}
                              />
                            }
                            label="Correct"
                            labelPlacement="start"
                          />
                        )
                      }}
                    />
                  </Grid>
                ))}
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Points"
                    type="number"
                    value={currentQuestion.points}
                    onChange={(e) => setCurrentQuestion({
                      ...currentQuestion,
                      points: parseInt(e.target.value)
                    })}
                    InputProps={{ inputProps: { min: 1, max: 100 } }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddQuestion}
                    fullWidth
                  >
                    Add Question
                  </Button>
                </Grid>
                
                {questions.length > 0 && (
                  <Grid item xs={12}>
                    <List>
                      {questions.map((q, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={q.question}
                            secondary={`${q.points} points - Correct: Option ${q.correctAnswer + 1}`}
                          />
                          <ListItemSecondaryAction>
                            <IconButton onClick={() => handleDeleteQuestion(index)}>
                              <Delete />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                )}
              </>
            )}
            
            {formData.taskType === 'code' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Coding Task Configuration
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Programming Language</InputLabel>
                    <Select
                      value={codeTemplate.language}
                      onChange={(e) => setCodeTemplate({
                        ...codeTemplate,
                        language: e.target.value
                      })}
                      label="Programming Language"
                    >
                      <MenuItem value="javascript">JavaScript</MenuItem>
                      <MenuItem value="python">Python</MenuItem>
                      <MenuItem value="java">Java</MenuItem>
                      <MenuItem value="cpp">C++</MenuItem>
                      <MenuItem value="csharp">C#</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    label="Starter Code"
                    value={codeTemplate.starterCode}
                    onChange={(e) => setCodeTemplate({
                      ...codeTemplate,
                      starterCode: e.target.value
                    })}
                    placeholder="Provide starter code template..."
                    sx={{ fontFamily: 'monospace' }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="subtitle1">Test Cases</Typography>
                    <Button
                      startIcon={<Add />}
                      onClick={handleAddTestCase}
                    >
                      Add Test Case
                    </Button>
                  </Box>
                  
                  {codeTemplate.testCases.map((testCase, index) => (
                    <Paper key={index} sx={{ p: 2, mb: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={5}>
                          <TextField
                            fullWidth
                            label="Input"
                            multiline
                            rows={2}
                            value={testCase.input}
                            onChange={(e) => {
                              const newTestCases = [...codeTemplate.testCases];
                              newTestCases[index].input = e.target.value;
                              setCodeTemplate({
                                ...codeTemplate,
                                testCases: newTestCases
                              });
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                          <TextField
                            fullWidth
                            label="Expected Output"
                            multiline
                            rows={2}
                            value={testCase.expectedOutput}
                            onChange={(e) => {
                              const newTestCases = [...codeTemplate.testCases];
                              newTestCases[index].expectedOutput = e.target.value;
                              setCodeTemplate({
                                ...codeTemplate,
                                testCases: newTestCases
                              });
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={testCase.isHidden}
                                onChange={(e) => {
                                  const newTestCases = [...codeTemplate.testCases];
                                  newTestCases[index].isHidden = e.target.checked;
                                  setCodeTemplate({
                                    ...codeTemplate,
                                    testCases: newTestCases
                                  });
                                }}
                              />
                            }
                            label="Hidden"
                          />
                          <IconButton onClick={() => handleDeleteTestCase(index)}>
                            <Delete />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Grid>
              </>
            )}
            
            <Divider sx={{ width: '100%', my: 2 }} />
            
            {/* Time and Scoring */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Time & Scoring
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Duration (in hours)"
                type="number"
                value={formData.duration.value}
                onChange={(e) => setFormData({
                  ...formData,
                  duration: {
                    value: parseInt(e.target.value) || 1,
                    unit: 'hours'
                  }
                })}
                InputProps={{
                  inputProps: { min: 1, max: 720 },
                  endAdornment: <InputAdornment position="end">hours</InputAdornment>
                }}
                helperText="Enter duration in hours (e.g., 24 for 1 day, 168 for 1 week)"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Deadline"
                name="deadline"
                type="datetime-local"
                value={formData.deadline}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Maximum Score"
                name="maxScore"
                type="number"
                value={formData.maxScore}
                onChange={handleChange}
                InputProps={{ inputProps: { min: 1, max: 1000 } }}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Passing Score"
                name="passingScore"
                type="number"
                value={formData.passingScore}
                onChange={handleChange}
                InputProps={{ inputProps: { min: 0, max: formData.maxScore } }}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    name="autoEvaluate"
                    checked={formData.autoEvaluate}
                    onChange={handleChange}
                  />
                }
                label="Auto-Evaluate"
              />
            </Grid>
            
            <Divider sx={{ width: '100%', my: 2 }} />
            
            {/* Evaluation Criteria */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Evaluation Criteria
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Criterion"
                value={currentCriterion.criterion}
                onChange={(e) => setCurrentCriterion({
                  ...currentCriterion,
                  criterion: e.target.value
                })}
              />
            </Grid>
            
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label="Weight (%)"
                type="number"
                value={currentCriterion.weight}
                onChange={(e) => setCurrentCriterion({
                  ...currentCriterion,
                  weight: parseInt(e.target.value)
                })}
                InputProps={{ inputProps: { min: 1, max: 100 } }}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Description"
                value={currentCriterion.description}
                onChange={(e) => setCurrentCriterion({
                  ...currentCriterion,
                  description: e.target.value
                })}
              />
            </Grid>
            
            <Grid item xs={12} sm={2}>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={handleAddCriterion}
                fullWidth
              >
                Add
              </Button>
            </Grid>
            
            {evaluationCriteria.length > 0 && (
              <Grid item xs={12}>
                <List>
                  {evaluationCriteria.map((criterion, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={`${criterion.criterion} (${criterion.weight}%)`}
                        secondary={criterion.description}
                      />
                      <ListItemSecondaryAction>
                        <IconButton onClick={() => handleDeleteCriterion(index)}>
                          <Delete />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
                <Typography variant="caption" color="text.secondary">
                  Total Weight: {evaluationCriteria.reduce((sum, c) => sum + c.weight, 0)}%
                </Typography>
              </Grid>
            )}
            
            <Divider sx={{ width: '100%', my: 2 }} />
            
            {/* Job Mapping */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Map to Jobs
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={jobs}
                getOptionLabel={(option) => `${option.title} - ${option.department}`}
                value={selectedJobs}
                onChange={(e, value) => setSelectedJobs(value)}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        label={option.title}
                        {...tagProps}
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Jobs"
                    placeholder="Choose jobs to map this task to"
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Pipeline Stage</InputLabel>
                <Select
                  name="pipelineStage"
                  value={formData.pipelineStage}
                  onChange={handleChange}
                  label="Pipeline Stage"
                >
                  <MenuItem value="Screening">Screening</MenuItem>
                  <MenuItem value="Assessment">Assessment</MenuItem>
                  <MenuItem value="Interview">Interview</MenuItem>
                  <MenuItem value="Final Review">Final Review</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Divider sx={{ width: '100%', my: 2 }} />
            
            {/* Resources */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Resources (Optional)
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Resource Title"
                value={currentResource.title}
                onChange={(e) => setCurrentResource({
                  ...currentResource,
                  title: e.target.value
                })}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Resource URL"
                value={currentResource.url}
                onChange={(e) => setCurrentResource({
                  ...currentResource,
                  url: e.target.value
                })}
              />
            </Grid>
            
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={currentResource.resourceType}
                  onChange={(e) => setCurrentResource({
                    ...currentResource,
                    resourceType: e.target.value
                  })}
                  label="Type"
                >
                  <MenuItem value="document">Document</MenuItem>
                  <MenuItem value="video">Video</MenuItem>
                  <MenuItem value="link">Link</MenuItem>
                  <MenuItem value="file">File</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={2}>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={handleAddResource}
                fullWidth
              >
                Add
              </Button>
            </Grid>
            
            {resources.length > 0 && (
              <Grid item xs={12}>
                <List>
                  {resources.map((resource, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={resource.title}
                        secondary={`${resource.resourceType}: ${resource.url}`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton onClick={() => handleDeleteResource(index)}>
                          <Delete />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Grid>
            )}
            
            {error && (
              <Grid item xs={12}>
                <Alert severity="error">{error}</Alert>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/tasks')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<Save />}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Create Task'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default CreateTask;
